/**
 * Console UI kit.
 *
 * Same brand primitives as the app and website, retuned for a dense admin
 * surface: smaller radii, tighter padding, tabular figures.
 */

import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
  type SelectHTMLAttributes,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Check, Info, Loader2, Search, X } from 'lucide-react';
import type { BookingStatus } from '../lib/types';
import { cn } from '../lib/format';
import { useUi } from '../store/ui';

/* -------------------------------------------------------------------------- */
/* Button                                                                     */
/* -------------------------------------------------------------------------- */

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'gold';
type Size = 'xs' | 'sm' | 'md';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-[#0f332a] text-white border-transparent hover:bg-[#0a241d]',
  secondary: 'bg-white text-[#12241e] border-[#e6e2da] hover:border-[#0f332a] hover:bg-[#fbfaf8]',
  ghost: 'bg-transparent text-[#0f332a] border-transparent hover:bg-[#eaf0ec]',
  danger: 'bg-[#b4453a] text-white border-transparent hover:brightness-95',
  gold: 'bg-[#c69b3f] text-[#12241e] border-transparent hover:brightness-105',
};

const SIZES: Record<Size, string> = {
  xs: 'px-2.5 py-1.5 text-[12px] gap-1.5',
  sm: 'px-3 py-2 text-[13px] gap-1.5',
  md: 'px-4 py-2.5 text-sm gap-2',
};

/**
 * `size` is omitted from the native attributes because the HTML `size` attribute
 * is a number and ours is a token — the two cannot coexist on one prop name.
 */
export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'size' | 'type'> {
  label?: string;
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
  children?: ReactNode;
  type?: 'button' | 'submit' | 'reset';
}

export function Button({
  label,
  variant = 'primary',
  size = 'sm',
  loading = false,
  fullWidth = false,
  icon,
  iconRight,
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-xl border font-bold whitespace-nowrap',
        'transition-all duration-150 active:scale-[0.98]',
        'disabled:cursor-not-allowed disabled:opacity-45 disabled:active:scale-100',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
    >
      {loading ? <Loader2 size={14} className="animate-spin" aria-hidden /> : icon}
      {label ?? children}
      {!loading && iconRight}
    </button>
  );
}

/** Square icon-only button, for row actions where a label would not fit. */
export function IconButton({
  label,
  icon,
  tone = 'neutral',
  onClick,
  disabled,
}: {
  label: string;
  icon: ReactNode;
  tone?: 'neutral' | 'danger';
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={cn(
        'inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-[#e6e2da] bg-white transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-40',
        tone === 'danger'
          ? 'text-[#b4453a] hover:border-[#b4453a] hover:bg-[#f8e7e4]'
          : 'text-[#33443c] hover:border-[#0f332a] hover:bg-[#f6f4f0]',
      )}
    >
      {icon}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* Badges                                                                     */
/* -------------------------------------------------------------------------- */

export type Tone = 'accent' | 'gold' | 'success' | 'warning' | 'danger' | 'neutral';

const TONES: Record<Tone, string> = {
  accent: 'bg-[#eaf0ec] text-[#0f332a]',
  gold: 'bg-[#f4e9d2] text-[#a8802e]',
  success: 'bg-[#e4f0ea] text-[#2f7d5d]',
  warning: 'bg-[#f8eeda] text-[#b0781f]',
  danger: 'bg-[#f8e7e4] text-[#b4453a]',
  neutral: 'bg-[#efebe4] text-[#33443c]',
};

export function Badge({
  label,
  tone = 'neutral',
  icon,
  className,
}: {
  label: string;
  tone?: Tone;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold tracking-[0.04em] whitespace-nowrap uppercase',
        TONES[tone],
        className,
      )}
    >
      {icon}
      {label}
    </span>
  );
}

const STATUS_TONES: Record<string, Tone> = {
  Pending: 'warning',
  Confirmed: 'accent',
  'On the Way': 'success',
  'In Progress': 'accent',
  Completed: 'success',
  Cancelled: 'danger',
};

export function StatusPill({ status }: { status: BookingStatus | string }) {
  return <Badge label={status} tone={STATUS_TONES[status] ?? 'neutral'} />;
}

/* -------------------------------------------------------------------------- */
/* Page scaffolding                                                           */
/* -------------------------------------------------------------------------- */

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-[24px] leading-tight font-bold tracking-[-0.02em] text-[#12241e] sm:text-[28px]">
          {title}
        </h1>
        {subtitle ? <p className="mt-1.5 text-[13.5px] text-[#5c6d64]">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

export function Panel({
  title,
  subtitle,
  actions,
  children,
  bodyClassName,
}: {
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  bodyClassName?: string;
}) {
  return (
    <section className="card overflow-hidden">
      {title ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#efebe4] bg-[#fbfaf8] px-5 py-3.5">
          <div className="min-w-0">
            <h2 className="text-[14.5px] font-bold text-[#12241e]">{title}</h2>
            {subtitle ? <p className="mt-0.5 text-[12px] text-[#71827a]">{subtitle}</p> : null}
          </div>
          {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

export function StatCard({
  label,
  value,
  delta,
  hint,
  icon,
  tone = 'accent',
}: {
  label: string;
  value: string;
  /** Signed percentage change against the previous period. */
  delta?: number;
  hint?: string;
  icon?: ReactNode;
  tone?: Tone;
}) {
  return (
    <div className="card p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[11px] font-bold tracking-[0.06em] text-[#71827a] uppercase">{label}</p>
        {icon ? (
          <span
            aria-hidden
            className={cn(
              'inline-flex size-8 shrink-0 items-center justify-center rounded-lg',
              TONES[tone],
            )}
          >
            {icon}
          </span>
        ) : null}
      </div>

      <p className="mt-2 text-[26px] leading-none font-bold tracking-[-0.02em] text-[#12241e] sm:text-[30px]">
        {value}
      </p>

      <div className="mt-2 flex items-center gap-2">
        {delta !== undefined ? (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-bold',
              delta >= 0 ? 'bg-[#e4f0ea] text-[#2f7d5d]' : 'bg-[#f8e7e4] text-[#b4453a]',
            )}
          >
            {delta >= 0 ? '↑' : '↓'} {Math.abs(delta).toFixed(1)}%
          </span>
        ) : null}
        {hint ? <span className="text-[11.5px] text-[#a4b0a8]">{hint}</span> : null}
      </div>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      {icon ? (
        <span className="inline-flex size-12 items-center justify-center rounded-full bg-[#e8ede7] text-[#0f332a]">
          {icon}
        </span>
      ) : null}
      <h3 className="text-[16px] font-bold text-[#12241e]">{title}</h3>
      {body ? <p className="max-w-sm text-[13px] leading-relaxed text-[#71827a]">{body}</p> : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Form controls                                                              */
/* -------------------------------------------------------------------------- */

export function Field({
  label,
  error,
  hint,
  required,
  children,
  className,
}: {
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: (props: { id: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) => ReactNode;
  className?: string;
}) {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <div className={className}>
      <label htmlFor={id} className="label">
        {label}
        {required ? (
          <span aria-hidden className="ml-0.5 text-[#b4453a]">
            *
          </span>
        ) : null}
      </label>

      {children({
        id,
        ...(error ? { 'aria-invalid': true } : {}),
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
      })}

      {error ? (
        <p id={`${id}-error`} role="alert" className="mt-1 text-[11.5px] font-semibold text-[#b4453a]">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-1 text-[11.5px] text-[#a4b0a8]">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * An error under a control that is not wrapped in `Field` (rows of a list
 * editor). Point the control's `aria-describedby` at `id`.
 */
export function InlineError({ id, children }: { id: string; children: ReactNode }) {
  return (
    <p id={id} role="alert" className="mt-1 text-[11.5px] font-semibold text-[#b4453a]">
      {children}
    </p>
  );
}

/** A labelled checkbox with an optional description, read as its description. */
export function Checkbox({
  checked,
  onChange,
  label,
  description,
  disabled,
  className,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
  className?: string;
}) {
  const id = useId();
  return (
    <label
      htmlFor={id}
      className={cn('flex cursor-pointer items-start gap-2.5', disabled && 'cursor-not-allowed opacity-60', className)}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        aria-describedby={description ? `${id}-description` : undefined}
        className="mt-0.5 size-4 shrink-0 accent-[#0f332a]"
      />
      <span className="min-w-0">
        <span className="block text-[13px] font-bold text-[#12241e]">{label}</span>
        {description ? (
          <span id={`${id}-description`} className="mt-0.5 block text-[11.5px] leading-relaxed text-[#71827a]">
            {description}
          </span>
        ) : null}
      </span>
    </label>
  );
}

export function TextInput({
  error,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { error?: boolean }) {
  return <input {...rest} className={cn('field', error && 'field-error', className)} />;
}

export function TextArea({
  error,
  className,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { error?: boolean }) {
  return (
    <textarea {...rest} className={cn('field resize-y leading-relaxed', error && 'field-error', className)} />
  );
}

export function Select({
  error,
  className,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { error?: boolean }) {
  return (
    <select {...rest} className={cn('field cursor-pointer', error && 'field-error', className)}>
      {children}
    </select>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  label,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  label: string;
}) {
  return (
    <div className="relative min-w-0 flex-1 sm:max-w-xs">
      <Search
        size={15}
        strokeWidth={2}
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-[#a4b0a8]"
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        placeholder={placeholder}
        className="field pl-9"
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="absolute top-1/2 right-2 -translate-y-1/2 rounded-full p-1 text-[#71827a] transition-colors hover:bg-[#f6f4f0]"
        >
          <X size={14} strokeWidth={2.2} aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
}) {
  return (
    <label
      className={cn(
        'flex items-start justify-between gap-4',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      )}
    >
      <span className="min-w-0">
        <span className="block text-[13.5px] font-bold text-[#12241e]">{label}</span>
        {description ? (
          <span className="mt-0.5 block text-[12px] leading-relaxed text-[#71827a]">
            {description}
          </span>
        ) : null}
      </span>

      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed',
          checked ? 'bg-[#0f332a]' : 'bg-[#d7e0d8]',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'inline-block size-4 rounded-full bg-white transition-transform',
            checked ? 'translate-x-6' : 'translate-x-1',
          )}
        />
      </button>
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/* Dialog behaviour                                                           */
/* -------------------------------------------------------------------------- */

interface OpenDialog {
  id: number;
  ref: RefObject<HTMLElement | null>;
}

/**
 * Open dialogs, innermost last. Only the top of the stack reacts to Escape and
 * traps Tab, so a confirm opened from inside an editor closes on its own without
 * taking the editor with it, and the page stays scroll-locked until the last
 * dialog closes.
 *
 * Dialogs render into `document.body` through a portal, so a modal opened from a
 * drawer is never clipped, transformed or stacked by the drawer it came from.
 */
const dialogStack: OpenDialog[] = [];
let dialogSeq = 0;

const FOCUSABLE = 'a[href], button, input, select, textarea, summary, [tabindex], [contenteditable="true"]';

/** Elements Tab can reach inside `root`, in document order. */
function tabbables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) =>
      element.tabIndex >= 0 &&
      // `:disabled` also covers controls inside a disabled fieldset.
      !element.matches(':disabled') &&
      !element.closest('[aria-hidden="true"], [inert]') &&
      element.getClientRects().length > 0,
  );
}

/** The innermost dialog still in the document. */
function topDialogElement(): HTMLElement | null {
  for (let index = dialogStack.length - 1; index >= 0; index -= 1) {
    const element = dialogStack[index]!.ref.current;
    if (element?.isConnected) return element;
  }
  return null;
}

function syncDialogState() {
  document.body.dataset.scrollLocked = dialogStack.length > 0 ? 'true' : 'false';

  // Only the innermost dialog is interactive or exposed as modal. This matters
  // for virtual-cursor navigation as well as keyboard focus when a confirmation
  // or stock operation is opened over an editor/drawer.
  const topId = dialogStack[dialogStack.length - 1]?.id;
  for (const entry of dialogStack) {
    const element = entry.ref.current;
    if (!element?.isConnected) continue;
    if (entry.id === topId) {
      element.removeAttribute('aria-hidden');
      element.removeAttribute('inert');
    } else {
      element.setAttribute('aria-hidden', 'true');
      element.setAttribute('inert', '');
    }
  }
}

/**
 * Focus moves into the dialog on open (an element marked `data-autofocus` wins,
 * then the first field or button) and back to whatever opened it on close.
 * Works whether the dialog closes through `open` or by being unmounted, and when
 * several dialogs close in the same render.
 */
function useDialog(open: boolean, onClose: () => void, ref: RefObject<HTMLElement | null>) {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;

    dialogSeq += 1;
    const id = dialogSeq;
    dialogStack.push({ id, ref });
    syncDialogState();
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const preferredFocus = () => {
      const root = ref.current;
      if (!root) return;
      const target =
        root.querySelector<HTMLElement>('[data-autofocus]') ??
        tabbables(root).find(
          (element) => !element.hasAttribute('data-close') && element.getAttribute('role') !== 'tab',
        ) ??
        root;
      target.focus();
    };

    const frame = window.requestAnimationFrame(() => {
      const root = ref.current;
      if (!root || root.contains(document.activeElement)) return;
      preferredFocus();
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (dialogStack[dialogStack.length - 1]?.id !== id) return;
      // A widget inside the dialog that handled the key (or an IME composing text)
      // keeps it.
      if (event.defaultPrevented || event.isComposing) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;

      const root = ref.current;
      if (!root) return;
      const focusables = tabbables(root);
      if (focusables.length === 0) {
        event.preventDefault();
        root.focus();
        return;
      }

      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;

      if (!root.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (!event.shiftKey && (active === last || active === root)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && (active === first || active === root)) {
        event.preventDefault();
        last.focus();
      }
    };

    const onFocusIn = (event: FocusEvent) => {
      if (dialogStack[dialogStack.length - 1]?.id !== id) return;
      const root = ref.current;
      if (root && event.target instanceof Node && !root.contains(event.target)) preferredFocus();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);

    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
      const index = dialogStack.findIndex((entry) => entry.id === id);
      if (index >= 0) dialogStack.splice(index, 1);
      syncDialogState();

      // Back to whatever opened the dialog. If that is gone (its row re-rendered,
      // or it lived in a dialog that closed too), stay inside the dialog now on top.
      const top = topDialogElement();
      if (opener?.isConnected && (!top || top.contains(opener))) opener.focus();
      else top?.focus();
    };
  }, [open, ref]);
}

/* -------------------------------------------------------------------------- */
/* Modal                                                                      */
/* -------------------------------------------------------------------------- */

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
  headerExtra,
  bodyClassName,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Rendered under the title row, above the scrolling body (e.g. section tabs). */
  headerExtra?: ReactNode;
  bodyClassName?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialog(open, onClose, ref);

  if (!open) return null;

  const width = { sm: 'sm:max-w-md', md: 'sm:max-w-xl', lg: 'sm:max-w-3xl', xl: 'sm:max-w-6xl' }[size];

  return createPortal(
    <div className="fixed inset-0 z-100 flex items-end justify-center sm:items-center sm:p-6">
      <button
        type="button"
        data-close
        tabIndex={-1}
        aria-hidden="true"
        onClick={onClose}
        className="animate-fade-in absolute inset-0 cursor-default bg-[#0a241d]/50 backdrop-blur-[2px]"
      />

      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'animate-fade-up relative z-10 flex max-h-[92dvh] w-full flex-col overflow-hidden bg-white shadow-[0_14px_26px_rgba(10,36,29,0.12)] outline-none',
          'rounded-t-2xl sm:rounded-2xl',
          width,
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-[#efebe4] px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-[16px] font-bold text-[#12241e]">
              {title}
            </h2>
            {subtitle ? (
              <p className="mt-0.5 text-[12.5px] leading-relaxed text-[#71827a]">{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            data-close
            onClick={onClose}
            aria-label="Close"
            className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-[#e6e2da] text-[#33443c] transition-colors hover:bg-[#f6f4f0]"
          >
            <X size={16} strokeWidth={2.2} aria-hidden />
          </button>
        </div>

        {headerExtra ? <div className="border-b border-[#efebe4]">{headerExtra}</div> : null}

        <div className={cn('scroll-slim min-h-0 flex-1 overflow-y-auto px-5 py-5', bodyClassName)}>
          {children}
        </div>

        {footer ? (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-[#efebe4] bg-[#fbfaf8] px-5 py-3.5">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

/* -------------------------------------------------------------------------- */
/* Drawer                                                                     */
/* -------------------------------------------------------------------------- */

/** A dialog that slides in from the right, for detail that sits beside a list. */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  headerExtra,
  size = 'md',
  bodyClassName,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  headerExtra?: ReactNode;
  size?: 'md' | 'lg';
  bodyClassName?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialog(open, onClose, ref);

  if (!open) return null;

  const width = { md: 'sm:max-w-2xl', lg: 'sm:max-w-4xl' }[size];

  return createPortal(
    <div className="fixed inset-0 z-100 flex justify-end">
      <button
        type="button"
        data-close
        tabIndex={-1}
        aria-hidden="true"
        onClick={onClose}
        className="animate-fade-in absolute inset-0 cursor-default bg-[#0a241d]/45 backdrop-blur-[2px]"
      />

      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'animate-slide-in-right relative z-10 flex h-dvh w-full flex-col bg-white shadow-[0_14px_26px_rgba(10,36,29,0.12)] outline-none',
          width,
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-[#efebe4] px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-[16px] font-bold text-[#12241e]">
              {title}
            </h2>
            {subtitle ? (
              <p className="mt-0.5 text-[12.5px] leading-relaxed text-[#71827a]">{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            data-close
            onClick={onClose}
            aria-label="Close"
            className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-[#e6e2da] text-[#33443c] transition-colors hover:bg-[#f6f4f0]"
          >
            <X size={16} strokeWidth={2.2} aria-hidden />
          </button>
        </div>

        {headerExtra ? <div className="border-b border-[#efebe4]">{headerExtra}</div> : null}

        <div className={cn('scroll-slim min-h-0 flex-1 overflow-y-auto px-5 py-5', bodyClassName)}>
          {children}
        </div>

        {footer ? (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-[#efebe4] bg-[#fbfaf8] px-5 py-3.5">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

/* -------------------------------------------------------------------------- */
/* Tabs                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * WAI-ARIA tabs with automatic activation: arrow keys, Home and End move between
 * tabs and select them. Pair each tab with a `TabPanel` of the same `idPrefix`.
 */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  idPrefix,
  className,
}: {
  /**
   * `countLabel` is read after the count by screen readers ("3 errors"), since a
   * bare number in a tab name says nothing.
   */
  tabs: { key: T; label: string; count?: number; countLabel?: string; attention?: boolean }[];
  value: T;
  onChange: (next: T) => void;
  label: string;
  idPrefix: string;
  className?: string;
}) {
  const refs = useRef(new Map<T, HTMLButtonElement>());

  function move(event: ReactKeyboardEvent, index: number) {
    const last = tabs.length - 1;
    let next: number | undefined;
    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    if (next === undefined) return;
    event.preventDefault();
    const tab = tabs[next]!;
    onChange(tab.key);
    refs.current.get(tab.key)?.focus();
  }

  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn('scroll-slim flex gap-1 overflow-x-auto', className)}
    >
      {tabs.map((tab, index) => {
        const selected = tab.key === value;
        return (
          <button
            key={tab.key}
            ref={(element) => {
              if (element) refs.current.set(tab.key, element);
              else refs.current.delete(tab.key);
            }}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${tab.key}`}
            aria-selected={selected}
            // Only the selected panel is rendered, so only its tab points at one.
            aria-controls={selected ? `${idPrefix}-panel-${tab.key}` : undefined}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.key)}
            onKeyDown={(event) => move(event, index)}
            className={cn(
              'relative inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-[13px] font-bold whitespace-nowrap transition-colors',
              selected
                ? 'border-[#0f332a] text-[#12241e]'
                : 'border-transparent text-[#71827a] hover:text-[#12241e]',
            )}
          >
            {tab.label}
            {tab.count !== undefined && tab.count > 0 ? (
              <span
                className={cn(
                  'inline-flex min-w-[18px] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold',
                  tab.attention ? 'bg-[#c69b3f] text-[#0a241d]' : 'bg-[#efebe4] text-[#33443c]',
                )}
              >
                <span aria-hidden>{tab.count}</span>
                <span className="sr-only">
                  {`, ${tab.count}${tab.countLabel ? ` ${tab.countLabel}` : ''}`}
                </span>
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({
  idPrefix,
  tabKey,
  children,
  className,
}: {
  idPrefix: string;
  tabKey: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      role="tabpanel"
      id={`${idPrefix}-panel-${tabKey}`}
      aria-labelledby={`${idPrefix}-tab-${tabKey}`}
      className={className}
    >
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Notice                                                                     */
/* -------------------------------------------------------------------------- */

const NOTICE_TONES = {
  info: 'bg-[#eaf0ec] text-[#0f332a]',
  warning: 'bg-[#f8eeda] text-[#8a5e15]',
  danger: 'bg-[#f8e7e4] text-[#b4453a]',
  neutral: 'bg-[#efebe4] text-[#33443c]',
} as const;

/** An inline message inside a form or panel. `alert` announces it when it appears. */
export function Notice({
  tone = 'info',
  children,
  alert = false,
  icon,
  className,
}: {
  tone?: keyof typeof NOTICE_TONES;
  children: ReactNode;
  alert?: boolean;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div
      role={alert ? 'alert' : undefined}
      className={cn(
        'flex items-start gap-2.5 rounded-xl px-3.5 py-2.5 text-[12.5px] leading-relaxed font-semibold',
        NOTICE_TONES[tone],
        className,
      )}
    >
      {icon ? (
        <span aria-hidden className="mt-0.5 shrink-0">
          {icon}
        </span>
      ) : null}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel = 'Confirm',
  destructive = false,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | boolean | Promise<void | boolean>;
  title: string;
  body: string;
  confirmLabel?: string;
  destructive?: boolean;
  /** A refusal to keep on screen, e.g. the server's reason after `onConfirm` returned false. */
  error?: string | null;
}) {
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) setPending(false);
  }, [open]);

  async function confirm() {
    if (pending) return;
    setPending(true);
    try {
      const result = await onConfirm();
      if (result !== false) onClose();
    } finally {
      setPending(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={pending ? () => undefined : onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" size="md" disabled={pending} onClick={onClose} label="Cancel" />
          <Button
            variant={destructive ? 'danger' : 'primary'}
            size="md"
            loading={pending}
            onClick={() => void confirm()}
            label={pending ? 'Working…' : confirmLabel}
          />
        </>
      }
    >
      <div className="flex gap-3.5">
        {destructive ? (
          <span
            aria-hidden
            className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-[#f8e7e4] text-[#b4453a]"
          >
            <AlertTriangle size={18} strokeWidth={2.2} />
          </span>
        ) : null}
        <p className="text-[13.5px] leading-relaxed text-[#33443c]">{body}</p>
      </div>
      {error ? (
        <Notice tone="danger" alert className="mt-4">
          {error}
        </Notice>
      ) : null}
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Pagination                                                                 */
/* -------------------------------------------------------------------------- */

export function usePagination<T>(rows: T[], perPage = 10) {
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(rows.length / perPage));

  // Filtering can shrink the list under the current page; step back rather than
  // showing an empty table.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const start = (page - 1) * perPage;
  const slice = rows.slice(start, start + perPage);

  return { page, setPage, pageCount, slice, start, total: rows.length };
}

export function Pagination({
  page,
  pageCount,
  total,
  start,
  shown,
  onChange,
}: {
  page: number;
  pageCount: number;
  total: number;
  start: number;
  shown: number;
  onChange: (next: number) => void;
}) {
  if (total === 0) return null;

  return (
    <nav
      aria-label="Pagination"
      className="flex flex-wrap items-center justify-between gap-3 border-t border-[#efebe4] px-5 py-3"
    >
      <p className="text-[12px] text-[#71827a]">
        Showing <span className="font-bold text-[#33443c]">{start + 1}</span>–
        <span className="font-bold text-[#33443c]">{start + shown}</span> of{' '}
        <span className="font-bold text-[#33443c]">{total}</span>
      </p>

      {pageCount > 1 ? (
        <div className="flex items-center gap-1">
          <Button
            size="xs"
            variant="secondary"
            disabled={page === 1}
            onClick={() => onChange(page - 1)}
            label="Previous"
          />
          <span aria-live="polite" className="px-2 text-[12px] font-bold text-[#33443c]">
            {page} / {pageCount}
          </span>
          <Button
            size="xs"
            variant="secondary"
            disabled={page === pageCount}
            onClick={() => onChange(page + 1)}
            label="Next"
          />
        </div>
      ) : null}
    </nav>
  );
}

/* -------------------------------------------------------------------------- */
/* Filter chips                                                               */
/* -------------------------------------------------------------------------- */

export function FilterChips<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { key: T; label: string; count?: number }[];
  value: T;
  onChange: (next: T) => void;
  label: string;
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          aria-pressed={value === option.key}
          onClick={() => onChange(option.key)}
          className={cn(
            'rounded-full border px-3 py-1.5 text-[12.5px] font-semibold whitespace-nowrap transition-colors',
            value === option.key
              ? 'border-[#0f332a] bg-[#0f332a] text-white'
              : 'border-[#e6e2da] bg-white text-[#33443c] hover:border-[#0f332a]',
          )}
        >
          {option.label}
          {option.count !== undefined ? (
            <span className="ml-1.5 text-[11px] opacity-70">{option.count}</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Toaster                                                                    */
/* -------------------------------------------------------------------------- */

const TOAST_TONES = {
  success: { cls: 'border-[#2f7d5d]/25 bg-[#e4f0ea]', Icon: Check },
  info: { cls: 'border-[#0f332a]/15 bg-white', Icon: Info },
  error: { cls: 'border-[#b4453a]/25 bg-[#f8e7e4]', Icon: AlertTriangle },
} as const;

export function Toaster() {
  const toasts = useUi((s) => s.toasts);
  const dismiss = useUi((s) => s.dismissToast);

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-5 z-90 flex flex-col items-center gap-2 px-4"
    >
      {toasts.map((item) => {
        const { cls, Icon } = TOAST_TONES[item.tone];
        return (
          <div
            key={item.id}
            role="status"
            className={cn(
              'animate-fade-up pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-xl border px-4 py-3 shadow-[0_8px_18px_rgba(10,36,29,0.08)]',
              cls,
            )}
          >
            <Icon size={15} strokeWidth={2.2} className="shrink-0 text-[#0f332a]" aria-hidden />
            <p className="flex-1 text-[13px] font-semibold text-[#12241e]">{item.message}</p>
            <button
              type="button"
              onClick={() => dismiss(item.id)}
              aria-label="Dismiss"
              className="shrink-0 rounded-full p-1 text-[#5c6d64] transition-colors hover:bg-black/5"
            >
              <X size={13} strokeWidth={2.2} aria-hidden />
            </button>
          </div>
        );
      })}
    </div>
  );
}
